import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  AccountBalanceWallet as CreditsIcon,
} from '@mui/icons-material';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import axiosInstance from '../../../api/axiosInstance';
import base_url from '../../../constants';
import useNotification from '../../common/Notification';

const Configurations = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [meta, setMeta] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
  });
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [editFormData, setEditFormData] = useState({
    business_id: '',
    waba_id: '',
    app_id: '',
    phone_number_id: '',
    whatsapp_phone_no: '',
    status: 'ACTIVE',
    process_status: 'ACTIVE',
    account_disabled: false,
  });
  const [creditsModalOpen, setCreditsModalOpen] = useState(false);
  const [creditsFormData, setCreditsFormData] = useState({
    bwc: '',
    dbwc: '',
    adbwc: '',
  });
  const [updatingCredits, setUpdatingCredits] = useState(false);
  const notify = useNotification();

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        per_page: rowsPerPage.toString(),
      });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);

      const response = await axiosInstance.get(
        `${base_url}/whatsapp/business/admin/configs?${params}`
      );

      if (response.data && response.data.success) {
        setConfigs(response.data.data.data || []);
        setMeta({
          current_page: response.data.data.current_page,
          per_page: response.data.data.per_page,
          total: response.data.data.total,
          last_page: response.data.data.last_page,
        });
      } else {
        throw new Error('Failed to fetch configurations');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch configurations';
      setError(errorMessage);
      console.error('Error fetching configs:', err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, statusFilter]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const handleEditClick = (config) => {
    setSelectedConfig(config);
    setEditFormData({
      business_id: config.business_id || '',
      waba_id: config.waba_id || '',
      app_id: config.app_id || '',
      phone_number_id: config.phone_number_id || '',
      whatsapp_phone_no: config.whatsapp_phone_no || '',
      status: config.status || 'ACTIVE',
      process_status: config.process_status || 'ACTIVE',
      account_disabled: config.user?.account_disabled || false,
    });
    setEditModalOpen(true);
  };

  const handleUpdate = async () => {
    if (!selectedConfig) return;

    try {
      const payload = {};
      if (editFormData.business_id) payload.business_id = editFormData.business_id;
      if (editFormData.waba_id) payload.waba_id = editFormData.waba_id;
      if (editFormData.app_id) payload.app_id = editFormData.app_id;
      if (editFormData.phone_number_id) payload.phone_number_id = editFormData.phone_number_id;
      if (editFormData.whatsapp_phone_no) payload.whatsapp_phone_no = editFormData.whatsapp_phone_no;
      payload.status = editFormData.status;
      payload.process_status = editFormData.process_status;
      if (editFormData.account_disabled !== undefined) payload.account_disabled = editFormData.account_disabled;

      const response = await axiosInstance.put(
        `${base_url}/whatsapp/business/admin/configs/${selectedConfig.user_id}`,
        payload
      );

      if (response.data && response.data.success) {
        notify('Configuration updated successfully', 'success');
        setEditModalOpen(false);
        fetchConfigs();
      } else {
        throw new Error('Failed to update configuration');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update configuration';
      notify(errorMessage, 'error');
      console.error('Error updating config:', err);
    }
  };

  const handleCreditsClick = async (config) => {
    setSelectedConfig(config);
    setCreditsModalOpen(true);
    setUpdatingCredits(true);

    try {
      // Fetch current config to get credit values
      const response = await axiosInstance.get(
        `${base_url}/whatsapp/business/admin/configs/${config.user_id}`
      );

      if (response.data && response.data.success) {
        const configData = response.data.data;
        setCreditsFormData({
          bwc: configData.bwc || '',
          dbwc: configData.dbwc || '',
          adbwc: configData.adbwc || '',
        });
      } else {
        // If fetch fails, set empty values
        setCreditsFormData({
          bwc: '',
          dbwc: '',
          adbwc: '',
        });
      }
    } catch (err) {
      // If fetch fails, set empty values
      setCreditsFormData({
        bwc: '',
        dbwc: '',
        adbwc: '',
      });
      console.error('Error fetching config credits:', err);
    } finally {
      setUpdatingCredits(false);
    }
  };

  const handleUpdateCredits = async () => {
    if (!selectedConfig) return;

    // Validate that at least one credit field is provided
    const hasBwc = creditsFormData.bwc !== '' && creditsFormData.bwc !== null;
    const hasDbwc = creditsFormData.dbwc !== '' && creditsFormData.dbwc !== null;
    const hasAdbwc = creditsFormData.adbwc !== '' && creditsFormData.adbwc !== null;

    if (!hasBwc && !hasDbwc && !hasAdbwc) {
      notify('At least one credit field must be provided', 'error');
      return;
    }

    setUpdatingCredits(true);

    try {
      const payload = {};
      if (hasBwc) payload.bwc = parseInt(creditsFormData.bwc, 10);
      if (hasDbwc) payload.dbwc = parseInt(creditsFormData.dbwc, 10);
      if (hasAdbwc) payload.adbwc = parseInt(creditsFormData.adbwc, 10);

      // Validate that values are non-negative
      if (payload.bwc !== undefined && payload.bwc < 0) {
        notify('BWC must be a non-negative integer', 'error');
        setUpdatingCredits(false);
        return;
      }
      if (payload.dbwc !== undefined && payload.dbwc < 0) {
        notify('DBWC must be a non-negative integer', 'error');
        setUpdatingCredits(false);
        return;
      }
      if (payload.adbwc !== undefined && payload.adbwc < 0) {
        notify('ADBWC must be a non-negative integer', 'error');
        setUpdatingCredits(false);
        return;
      }

      const response = await axiosInstance.put(
        `${base_url}/whatsapp/business/admin/configs/${selectedConfig.user_id}/credits`,
        payload
      );

      if (response.data && response.data.success) {
        notify('Credits updated successfully', 'success');
        setCreditsModalOpen(false);
        fetchConfigs();
      } else {
        throw new Error('Failed to update credits');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update credits';
      notify(errorMessage, 'error');
      console.error('Error updating credits:', err);
    } finally {
      setUpdatingCredits(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search by user name or email..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
          <TextField
            select
            label="Status"
            size="small"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="INACTIVE">Inactive</MenuItem>
          </TextField>
          <IconButton onClick={fetchConfigs} color="primary">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>User ID</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>User Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Email</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Business ID</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>WABA ID</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Phone Number</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Process Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Is Valid</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Last Heartbeat</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 3 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : configs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 3 }}>
                  <Typography color="textSecondary">No configurations found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              configs.map((config) => (
                <TableRow key={config.id} hover>
                  <TableCell>{config.user_id}</TableCell>
                  <TableCell>{config.user?.name || '-'}</TableCell>
                  <TableCell>{config.user?.email || '-'}</TableCell>
                  <TableCell>{config.business_id || '-'}</TableCell>
                  <TableCell>{config.waba_id || '-'}</TableCell>
                  <TableCell>{config.whatsapp_phone_no || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={config.status}
                      size="small"
                      color={config.status === 'ACTIVE' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={config.process_status}
                      size="small"
                      color={config.process_status === 'ACTIVE' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={config.is_valid ? 'Yes' : 'No'}
                      size="small"
                      color={config.is_valid ? 'success' : 'error'}
                    />
                  </TableCell>
                  <TableCell>{formatDate(config.last_heartbeat)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => handleEditClick(config)} title="Edit Configuration">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleCreditsClick(config)} title="Update Credits">
                        <CreditsIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={meta.total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 15, 25, 50]}
      />

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit WhatsApp Business Configuration</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Business ID"
              value={editFormData.business_id}
              onChange={(e) => setEditFormData({ ...editFormData, business_id: e.target.value })}
              fullWidth
            />
            <TextField
              label="WABA ID"
              value={editFormData.waba_id}
              onChange={(e) => setEditFormData({ ...editFormData, waba_id: e.target.value })}
              fullWidth
            />
            <TextField
              label="App ID"
              value={editFormData.app_id}
              onChange={(e) => setEditFormData({ ...editFormData, app_id: e.target.value })}
              fullWidth
            />
            <TextField
              label="Phone Number ID"
              value={editFormData.phone_number_id}
              onChange={(e) => setEditFormData({ ...editFormData, phone_number_id: e.target.value })}
              fullWidth
            />
            <TextField
              label="WhatsApp Phone Number"
              value={editFormData.whatsapp_phone_no}
              onChange={(e) => setEditFormData({ ...editFormData, whatsapp_phone_no: e.target.value })}
              fullWidth
              inputProps={{ maxLength: 50 }}
            />
            <TextField
              select
              label="Status"
              value={editFormData.status}
              onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
              fullWidth
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
            </TextField>
            <TextField
              select
              label="Process Status"
              value={editFormData.process_status}
              onChange={(e) => setEditFormData({ ...editFormData, process_status: e.target.value })}
              fullWidth
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
            </TextField>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={editFormData.account_disabled}
                    onChange={(e) => setEditFormData({ ...editFormData, account_disabled: e.target.checked })}
                  />
                }
                label="Disable User Account"
              />
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, ml: 4 }}>
                Disabling the account will prevent the user from logging in
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdate} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Credits Update Modal */}
      <Dialog open={creditsModalOpen} onClose={() => setCreditsModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Update Credits
          {selectedConfig && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
              User: {selectedConfig.user?.name || selectedConfig.user_id} ({selectedConfig.user?.email || '-'})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Alert severity="info" sx={{ mb: 1 }}>
              At least one credit field must be provided. Leave fields empty to keep current values.
            </Alert>
            <TextField
              label="Business WhatsApp Credits (BWC)"
              type="number"
              value={creditsFormData.bwc}
              onChange={(e) => setCreditsFormData({ ...creditsFormData, bwc: e.target.value })}
              fullWidth
              inputProps={{ min: 0 }}
              helperText="Minimum: 0"
            />
            <TextField
              label="Daily Business WhatsApp Credits (DBWC)"
              type="number"
              value={creditsFormData.dbwc}
              onChange={(e) => setCreditsFormData({ ...creditsFormData, dbwc: e.target.value })}
              fullWidth
              inputProps={{ min: 0 }}
              helperText="Minimum: 0"
            />
            <TextField
              label="Additional Daily Business WhatsApp Credits (ADBWC)"
              type="number"
              value={creditsFormData.adbwc}
              onChange={(e) => setCreditsFormData({ ...creditsFormData, adbwc: e.target.value })}
              fullWidth
              inputProps={{ min: 0 }}
              helperText="Minimum: 0"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreditsModalOpen(false)} disabled={updatingCredits}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateCredits} 
            variant="contained" 
            disabled={updatingCredits}
            startIcon={updatingCredits ? <CircularProgress size={16} /> : null}
          >
            {updatingCredits ? 'Updating...' : 'Update Credits'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Configurations;


